import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import Workspace from "@/models/workspace/Workspace";
import { User } from "@/models/users/User";
import { CustomColumnDef } from "@/components/data-table/types/custom-column-def";
import { CustomCellContext } from "@/components/data-table/types/custom-cell-context";
import { cn } from "@/lib/utils";
import SubscribeButton from "@/components/buttons/SubscribeButton";
import SubscribeAsGroupButton from "@/components/buttons/SubscribeAsGroupButton";
import {
  Subscription,
  SubscriptionCategory,
} from "@/models/subscriptions/Subscription";
import { ParentTenantInformation } from "@/models/tenant/tenant";
import UserAvatar from "@/components/images/UserAvatar";
import {
  formatDateToDanishLocaleLongMonthWithTime,
  formatUsersName,
} from "@/utils/format";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import useSubscriptionOperationStore from "@/stores/subscription.store";
import Tenant from "@/models/tenant/tenant";
import { HighlightedText } from "@/components/highlighted-text/HighlightedText";
import { workspaceDescriptionMatch } from "./workspaceDescriptionMatch";

export const getColumns = (
  tenantUsers: User[],
  isDarkMode: boolean,
  parentTenant: ParentTenantInformation | null,
  subscriptions: Subscription[],
  availableSubtenants: Tenant[],
  currentTenant: Tenant | null,
  onOpenSharedAccessDialog: (sharedUserIds: string[]) => void,
  onOpenSubTenantsDialog: (tenantNames: string[]) => void,
  hasValidDarkLogo: boolean,
  hasValidLightLogo: boolean,
  darkLogo: string,
  lightLogo: string,
  groupSubscriptionEntityPaths: Set<string> | undefined,
  hasCreateFeatureGroups: boolean,
  /** The search the rows were fetched for: marked in the name, and shown in the description when found there. */
  searchTerm?: string
): CustomColumnDef<Workspace>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Navn" />
    ),
    meta: {
      displayName: "Navn",
      dataType: "text",
    },
    cell: ({ row, isHovering }: CustomCellContext<Workspace>) => {
      const name = row.original.name;
      const rowEntityPath = `workspace/${row.original.id}`;

      const isSubscribed =
        Array.isArray(subscriptions) &&
        subscriptions.some((s) => s.entityPath === rowEntityPath);

      const isGroupSubscribed =
        !!groupSubscriptionEntityPaths?.has(rowEntityPath);

      const isSubscribedLoading = useSubscriptionOperationStore
        .getState()
        .isPending(rowEntityPath);

      // The search matches descriptions too, which the table does not show: without
      // this a workspace found there looks like a random hit.
      const descriptionMatch = workspaceDescriptionMatch(
        row.original.description,
        searchTerm
      );

      return (
        <div className="flex flex-1 items-center justify-between gap-2 relative">
          <div
            className={cn(
              "w-full min-w-60",
              "group-hover:max-w-[calc(100%-4.5rem)]",
              isSubscribed && isSubscribedLoading && "max-w-[calc(100%-2rem)]",
              isSubscribed && "max-w-[calc(100%-4.5rem)]"
            )}
          >
            <p className="line-clamp-1">
              <HighlightedText text={name} searchTerm={searchTerm} />
            </p>
            {descriptionMatch && (
              <p className="line-clamp-1 text-xs text-muted-foreground">
                <HighlightedText
                  text={descriptionMatch}
                  searchTerm={searchTerm}
                />
              </p>
            )}
          </div>
          <div className={cn("absolute right-0 flex flex-row gap-1")}>
            <div
              className={cn(
                "flex flex-row",
                !(isHovering || isSubscribed || isSubscribedLoading) && "hidden"
              )}
            >
              <SubscribeButton
                subscribeCategory={SubscriptionCategory.Workspace}
                entityPath={rowEntityPath}
                source="workspace"
                isSubscribed={isSubscribed}
                entityName={name}
              />
            </div>
            <div
              className={cn(
                "flex flex-row",
                !isGroupSubscribed &&
                  !(isHovering && hasCreateFeatureGroups) &&
                  "hidden"
              )}
            >
              <SubscribeAsGroupButton
                subscribeCategory={SubscriptionCategory.Workspace}
                entityPath={rowEntityPath}
                source="workspace"
                entityName={name}
                isGroupSubscribed={isGroupSubscribed}
              />
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "documents",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Antal filer" />
    ),
    cell: ({ row }: { row: { original: Workspace } }) => {
      const countDocuments = (folders: Workspace["folders"]): number => {
        return folders.reduce((total, folder) => {
          const folderDocCount = folder.documents.length;
          const nestedCount = countDocuments(folder.folders);
          return total + folderDocCount + nestedCount;
        }, 0);
      };

      const workspace = row.original;
      const directDocCount = workspace.documents.length;
      const folderDocCount = countDocuments(workspace.folders);
      return directDocCount + folderDocCount;
    },
    meta: {
      displayName: "Antal filer",
      dataType: "number",
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Oprettet" />
    ),
    cell: ({ row }: { row: { original: Workspace } }) => {
      const workspace = row.original;
      if (!workspace.createdAt) return "";
      return formatDateToDanishLocaleLongMonthWithTime(
        new Date(workspace.createdAt)
      );
    },
    meta: {
      displayName: "Oprettet",
      dataType: "date",
    },
  },
  {
    accessorKey: "updatedAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Opdateret" />
    ),
    cell: ({ row }: { row: { original: Workspace } }) => {
      const workspace = row.original;
      if (!workspace.updatedAt) return "";
      return formatDateToDanishLocaleLongMonthWithTime(
        new Date(workspace.updatedAt)
      );
    },
    meta: {
      displayName: "Opdateret",
      dataType: "date",
    },
  },
  {
    accessorKey: "owner",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Ejer" />
    ),
    cell: ({ row }: { row: { original: Workspace } }) => {
      if (!row.original.owner) return null;

      return (
        <div className="flex items-center gap-2">
          <UserAvatar
            user={row.original.owner}
            avatarClassName="size-6 border-2 border-white"
          />
          <span>{formatUsersName(row.original.owner)}</span>
        </div>
      );
    },
    meta: {
      displayName: "Ejer",
      dataType: "text",
    },
  },
  {
    accessorKey: "sharedAccess",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Delt adgang" />
    ),
    cell: ({ row }: { row: { original: Workspace } }) => {
      const maxDisplayUsers = 5;
      const workspace = row.original;
      const sharedUserIds = Object.keys(workspace.sharedAccess).filter(
        (key) => key !== workspace.userId
      );
      const hasSharedTenantAccess = workspace.sharedWithWholeTenant;
      const isSharedByParent =
        workspace.sharedWithSubTenants &&
        workspace.sharedWithSubTenants.length > 0;
      const displayedUsers = sharedUserIds.slice(0, maxDisplayUsers - 1); // Adjust based on maxDisplayUsers
      const remainingUsersCount = sharedUserIds.length - displayedUsers.length;

      // Get tenant names for sharedWithSubTenants
      const getTenantNames = (tenantIds: string[]): string[] => {
        return tenantIds
          .map((id) => availableSubtenants.find((t) => t.id === id)?.name)
          .filter((name): name is string => !!name);
      };

      const maxDisplayInTooltip = 5;
      const tenantNames = workspace.sharedWithSubTenants
        ? getTenantNames(workspace.sharedWithSubTenants)
        : [];

      const displayedTenantNames = tenantNames.slice(0, maxDisplayInTooltip);
      const remainingTenantsCount =
        tenantNames.length - displayedTenantNames.length;

      return (
        <div className="flex items-center gap-2 flex-wrap">
          {/* Subtenants: workspace shared with specific sub-organisations */}
          {workspace.sharedWithSubTenants &&
            workspace.sharedWithSubTenants.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenSubTenantsDialog(
                        tenantNames.length > 0
                          ? tenantNames
                          : workspace.sharedWithSubTenants!.map(
                              (id) =>
                                availableSubtenants.find((t) => t.id === id)
                                  ?.name ?? id
                            )
                      );
                    }}
                  >
                    {workspace.sharedWithSubTenants.length} underorganisation
                    {workspace.sharedWithSubTenants.length > 1 ? "er" : ""}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-col gap-1">
                    {displayedTenantNames.length > 0 ? (
                      <>
                        {displayedTenantNames.map((name, index) => (
                          <span key={index}>{name}</span>
                        ))}
                        {remainingTenantsCount > 0 && (
                          <span>
                            +{remainingTenantsCount} flere - klik for at udvide
                          </span>
                        )}
                      </>
                    ) : (
                      <span>
                        Delt med {workspace.sharedWithSubTenants.length}{" "}
                        underorganisation
                        {workspace.sharedWithSubTenants.length > 1 ? "er" : ""}
                      </span>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}

          {/* Whole tenant: workspace shared with entire organisation — logo only (org name hidden; shown as fallback if image fails or no logo URL) */}
          {hasSharedTenantAccess && currentTenant && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 shrink-0 min-h-6">
                  {hasValidDarkLogo && hasValidLightLogo ? (
                    isDarkMode ? (
                      <div className="flex items-center gap-2">
                        <img
                          src={darkLogo}
                          className="max-h-6 h-6 w-auto object-contain"
                          alt=""
                          aria-hidden
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const fallback = e.currentTarget
                              .nextElementSibling as HTMLElement | null;
                            if (fallback) fallback.style.display = "";
                          }}
                        />
                        <span
                          className="text-sm font-medium text-muted-foreground truncate max-w-32"
                          style={{ display: "none" }}
                        >
                          {currentTenant.name ?? "Hele organisationen"}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <img
                          src={lightLogo}
                          className="max-h-6 h-6 w-auto object-contain"
                          alt=""
                          aria-hidden
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const fallback = e.currentTarget
                              .nextElementSibling as HTMLElement | null;
                            if (fallback) fallback.style.display = "";
                          }}
                        />
                        <span
                          className="text-sm font-medium text-muted-foreground truncate max-w-32"
                          style={{ display: "none" }}
                        >
                          {currentTenant.name ?? "Hele organisationen"}
                        </span>
                      </div>
                    )
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground truncate max-w-32">
                      {currentTenant.name ?? "Hele organisationen"}
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>Delt med hele organisationen</TooltipContent>
            </Tooltip>
          )}

          {/* Shared via parent with subtenants: show parent org (when viewing as sub-tenant) */}
          {isSharedByParent && !hasSharedTenantAccess && parentTenant && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 shrink-0">
                  {parentTenant.parentTenantLogo?.dark &&
                  parentTenant.parentTenantLogo?.light ? (
                    isDarkMode ? (
                      <img
                        src={parentTenant.parentTenantLogo.dark}
                        className="max-h-6"
                        alt={parentTenant.name ?? ""}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fallback = e.currentTarget
                            .nextElementSibling as HTMLElement | null;
                          if (fallback) fallback.style.display = "";
                        }}
                      />
                    ) : (
                      <img
                        src={parentTenant.parentTenantLogo.light}
                        className="max-h-6"
                        alt={parentTenant.name ?? ""}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fallback = e.currentTarget
                            .nextElementSibling as HTMLElement | null;
                          if (fallback) fallback.style.display = "";
                        }}
                      />
                    )
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground truncate max-w-32">
                      {parentTenant.name ?? "Hele organisationen"}
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {parentTenant.name ?? "Hele organisationen"}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Individual users (sharedAccess): workspace shared with specific users */}
          {sharedUserIds.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSharedAccessDialog(sharedUserIds);
                  }}
                  className="cursor-pointer flex items-center gap-1"
                >
                  <div className="flex -space-x-3 items-center">
                    {displayedUsers.map((key, index) => {
                      const user = tenantUsers.find((u) => u.id === key);
                      if (!user) return null;
                      return (
                        <UserAvatar
                          key={key}
                          user={user}
                          avatarClassName="size-6 border-2 border-white flex"
                          avatarStyle={{
                            zIndex: displayedUsers.length - index,
                          }}
                          fallbackClassName="text-[10px]"
                        />
                      );
                    })}
                  </div>
                  {remainingUsersCount > 0 && (
                    <div className="text-sm ml-.5 cursor-pointer group-hover:underline">
                      +{remainingUsersCount}
                    </div>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>Delt med brugere</TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
    enableSorting: false,
    meta: {
      displayName: "Delt adgang",
    },
  },
];
